import { Link, useNavigate, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import { getSite } from "@/data/site";
import { getServices } from "@/data/services";
import { useLang } from "@/i18n/lang";
import { Reveal } from "@/lib/ui";

export default function Footer() {
  const { lang } = useLang();
  const { CONTACT, ui } = getSite(lang);
  const services = getServices(lang).slice(0, 6);
  const navigate = useNavigate();
  const location = useLocation();
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  const goTo = (to) => {
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") { navigate("/"); setTimeout(() => scrollToId(id), 500); }
      else scrollToId(id);
    } else navigate(to);
  };
  return (
    <footer className="relative bg-ink text-white pt-28 pb-10 overflow-hidden grain" data-testid="footer">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[80vw] h-80 bg-gold/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <Reveal><h2 className="font-serif font-light leading-none text-[15vw] md:text-[11vw] tracking-tighter">{ui.footerTagPre}<span className="gold-text italic">{ui.footerTagGold}</span></h2></Reveal>
        <div className="mt-20 grid md:grid-cols-4 gap-12 border-t border-white/10 pt-14">
          <div className="md:col-span-1">
            <img src="/logo-full.png" alt="Glamour Dent" className="h-12 w-auto mb-5" />
            <p className="text-white/50 text-sm leading-relaxed font-light max-w-xs">{ui.footerAbout}</p>
            <div className="flex gap-3 mt-6">{[{ Icon: Instagram, href: "https://www.instagram.com/glamourdent_clinic/", label: "Instagram" }, { Icon: Facebook, href: "https://www.facebook.com/profile.php?id=61558979597790", label: "Facebook" }].map(({ Icon, href, label }) => (<a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`footer-social-${label.toLowerCase()}`} className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"><Icon className="w-4 h-4" /></a>))}</div>
          </div>
          <div>
            <h4 className="font-accent text-xs tracking-[0.22em] uppercase text-gold mb-6">{ui.footerServices}</h4>
            <ul className="space-y-3">{services.map((s) => (<li key={s.slug}><Link to={`/services/${s.slug}`} className="text-white/60 hover:text-white text-sm transition-colors">{s.name}</Link></li>))}</ul>
          </div>
          <div>
            <h4 className="font-accent text-xs tracking-[0.22em] uppercase text-gold mb-6">{ui.footerExplore}</h4>
            <ul className="space-y-3">{ui.footerLinks.map(([l, h]) => (<li key={l}>{h.startsWith("/#") ? (<button onClick={() => goTo(h)} className="text-white/60 hover:text-white text-sm transition-colors text-left">{l}</button>) : (<Link to={h} className="text-white/60 hover:text-white text-sm transition-colors">{l}</Link>)}</li>))}</ul>
          </div>
          <div>
            <h4 className="font-accent text-xs tracking-[0.22em] uppercase text-gold mb-6">{ui.footerContact}</h4>
            <ul className="space-y-4 text-sm text-white/60">
              <li className="flex gap-3"><Phone className="w-4 h-4 text-gold shrink-0 mt-0.5" /><a href={CONTACT.phoneHref} className="hover:text-white transition-colors">{CONTACT.phone}</a></li>
              <li className="flex gap-3"><Mail className="w-4 h-4 text-gold shrink-0 mt-0.5" /><a href={`mailto:${CONTACT.email}`} className="hover:text-white transition-colors">{CONTACT.email}</a></li>
              <li className="flex gap-3"><MapPin className="w-4 h-4 text-gold shrink-0 mt-0.5" />{CONTACT.address}</li>
            </ul>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-white/40 font-accent tracking-wide">
          <p>© {new Date().getFullYear()} Glamour Dent. {ui.rights}</p><p>{ui.craft}</p>
        </div>
      </div>
    </footer>
  );
}
